import { useRef, useCallback, useState, useEffect } from 'react'
import { FormHandles } from '@unform/core'
import { Form } from '@unform/web'
import * as Yup from 'yup'

import moment from 'moment'

import { ConfirmButton, InputWrapper } from './styles'
import { useDispatch, useSelector } from 'react-redux'

import { toast } from 'react-toastify'

import states from './states.json'
import { RootState } from 'store/modules/rootReducer'
import { editStudentRequest } from 'store/modules/student/actions'
import getValidationError from 'utils/getValidationErrors'
import Input from 'components/Input'
import { cepMask, cpfMask, telMask } from 'utils/masks'
import Select from 'components/Select'
import { cpfValidator } from 'utils/cpfValidator'
import ZipCodeApi from 'services/zipcode'
import { CHANGE_PASSWORD } from 'pages/Profile/services'

interface PassStudentConfig {
  password?: string
  newPassword?: string
  confirmNewPass?: string
}

const GeralForm: React.FC = () => {
  const [infoLoading, setInfoLoading] = useState<boolean>(false)
  const [passLoading, setPassLoading] = useState<boolean>(false)
  const [blockAddress, setBlockAddress] = useState<boolean>(false)
  const [zipcode, setZipcode] = useState<string>('')

  const formRef = useRef<FormHandles>()
  const passFormRef = useRef<FormHandles>()
  const { profile } = useSelector((state: RootState) => state.student)
  const dispatch = useDispatch()

  const handleEditStudent = useCallback(
    async (data: any) => {
      setInfoLoading(true)
      try {
        formRef.current?.setErrors({})

        const schema = Yup.object().shape({
          name: Yup.string().required('Informe seu nome'),
          email: Yup.string().required('Informe seu e-mail').email(),
          document: Yup.string()
            .required('Informe seu CPF')
            .test('test-invalid-cpf', 'CPF inválido', (document) => cpfValidator(document)),
          birthdate: Yup.string()
            .required('Informe sua data de nascimento')
            .test('test-date', 'Digite uma data válida', (value) => {
              const date = value.split('/')
              if (moment(`${date[2]}/${date[1]}/${date[0]}`).isAfter(moment())) return false
              return moment(`${date[2]}/${date[1]}/${date[0]}`).isValid()
            }),
          telephone: Yup.string().required('Informe seu telefone'),
          zipcode: Yup.string().required('Informe seu CEP'),
          street: Yup.string().required('Informe seu logradouro'),
          state: Yup.string().required('Informe seu estado'),
          number: Yup.string().required('Informe o número'),
          district: Yup.string().required('Informe o seu bairro'),
          city: Yup.string().required('Informe a sua cidade'),
        })

        await schema.validate(data, {
          abortEarly: false,
        })

        dispatch(
          editStudentRequest({
            email: data?.email,
            name: data?.name,
            telephone: data?.telephone,
            document: data?.document,
            birthdate: data?.birthdate.split('/').reverse().join('-'),
            address: {
              addressComplement: data?.addressComplement,
              city: data?.city,
              district: data?.district,
              number: data?.number,
              state: data?.state,
              street: data?.street,
              zipcode: data?.zipcode,
            },
            avatar: profile.avatar,
          })
        )

        toast.success('Dados alterados com sucesso')
      } catch (err) {
        if (err?.response?.status === 401) return
        console.error(err)

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationError(err)
          formRef.current?.setErrors(errors)
        }

        return toast.error('Algo deu errado, tente novamente')
      } finally {
        setInfoLoading(false)
      }
    },
    [dispatch, profile.avatar]
  )

  const handleChangePass = async (passData: PassStudentConfig, { reset }) => {
    setPassLoading(true)
    try {
      passFormRef.current?.setErrors({})

      const passSchema = Yup.object().shape({
        password: Yup.string().required('Informe sua senha'),
        newPassword: Yup.string()
          .required('Informe a sua nova senha')
          .min(6, 'Senha deve conter pelo menos seis caracteres'),
        confirmNewPass: Yup.string()
          .min(6, 'Senha deve conter pelo menos seis caracteres')
          .oneOf([Yup.ref('newPassword'), null], 'As senhas devem ser iguais'),
      })

      await passSchema.validate(passData, {
        abortEarly: false,
      })

      await CHANGE_PASSWORD(passData.password, passData.newPassword)

      passFormRef.current?.setErrors({})
      reset()
      setPassLoading(false)
      toast.success('Senha alterada com sucesso')
    } catch (err) {
      setPassLoading(false)
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationError(err)
        passFormRef.current?.setErrors(errors)
      }

      if (err.response) {
        if (err.response.status === 401) return
        const { data } = err.response

        if (data.error === 'Invalid Password')
          return toast.error('A senha informada está incorreta')

        return toast.error('Algo deu errado, tente novamente')
      }
    }
  }

  const getAddress = useCallback(async () => {
    try {
      const {
        data: { logradouro, bairro, localidade, uf },
      } = await ZipCodeApi(`${zipcode.replace('.', '').replace('-', '')}/json`)

      formRef.current.setData({
        street: logradouro,
        district: bairro,
        city: localidade,
        state: uf,
      })

      setBlockAddress(true)
    } catch (error) {
      setBlockAddress(false)
      toast.info('Não foi possível buscar o seu endereço, por favor, entre com as informações')
    }
  }, [zipcode])

  useEffect(() => {
    if (zipcode.length === 10) {
      getAddress()
    }

    if (profile.address) {
      setBlockAddress(true)
    }
  }, [getAddress, profile.address, zipcode])

  return (
    <>
      <Form ref={formRef} onSubmit={handleEditStudent} autoComplete="false">
        <h2>Informações da Conta</h2>
        <hr />
        <Input name="name" label="Nome Completo" defaultValue={profile?.name} />
        <Input name="email" label="E-mail" type="email" defaultValue={profile?.email} />
        <Input
          name="birthdate"
          label="Data de nascimento"
          defaultValue={
            profile?.birthdate?.toString().split('T')[0].split('-').reverse().join('/') || ''
          }
          isDate
        />
        <InputWrapper>
          <div>
            <Input
              name="document"
              label="CPF"
              defaultValue={cpfMask(profile?.document)}
              isCpf
              disabled={!!profile?.document}
              information={
                profile?.document
                  ? "Para alterar o CPF, entre em contato com o <a href='my-profile?title=help'>suporte</a>"
                  : null
              }
            />
          </div>
          <div>
            <Input
              name="telephone"
              label="Telefone"
              defaultValue={telMask(profile?.telephone || '')}
              isPhone
            />
          </div>
        </InputWrapper>
        <h2>Endereço</h2>
        <hr />
        <Input
          name="zipcode"
          label="CEP"
          defaultValue={cepMask(profile?.address?.zipcode ?? '')}
          onChange={(e) => setZipcode(e.target.value)}
          isCep
        />
        <Input
          name="street"
          label="Rua, Avenida, etc."
          defaultValue={profile?.address?.street ?? ''}
          disabled={blockAddress}
        />
        <InputWrapper>
          <div>
            <Input name="number" label="Numero" defaultValue={profile?.address?.number ?? ''} />
          </div>
          <div>
            <Input
              name="district"
              label="Bairro"
              defaultValue={profile?.address?.district ?? ''}
              disabled={blockAddress}
            />
          </div>
        </InputWrapper>
        <Input
          name="addressComplement"
          label="Complemento"
          defaultValue={profile?.address?.addressComplement ?? ''}
        />
        <InputWrapper>
          <div>
            <Select
              disabled={blockAddress}
              name="state"
              options={states}
              label="Estado"
              defaultValue={profile?.address?.state ?? ''}
            />
          </div>
          <div>
            <Input
              name="city"
              label="Cidade"
              disabled={blockAddress}
              defaultValue={profile?.address?.city ?? ''}
            />
          </div>
        </InputWrapper>
        <ConfirmButton type="submit" disabled={infoLoading}>
          {infoLoading ? 'Aguarde...' : 'Salvar alterações'}
        </ConfirmButton>
      </Form>

      <Form ref={passFormRef} onSubmit={handleChangePass} autoComplete="false">
        <h2>Senha</h2>
        <hr />
        <Input name="password" label="Senha atual" type="password" />
        <Input name="newPassword" label="Nova Senha" type="password" />
        <Input name="confirmNewPass" label="Confirme a nova senha" type="password" />
        <ConfirmButton type="submit">
          {passLoading ? 'Aguarde...' : 'Salvar alterações'}
        </ConfirmButton>
      </Form>
    </>
  )
}

export default GeralForm
